/**
 * Configure post-login home for a tenant (launchpad, Studio app, legacy app, or CRM).
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { listV2Apps } from '../api/studioV2.api';
import { listDashboardConfigs } from '../api/dashboardConfig.api';
import { putTenantHomeDestination } from '../utils/resolveTenantHome';
import { canAccessLegacyBuilders } from '../config/domainExperience';

const HOME_TYPES = [
  { value: 'launchpad', label: 'Solution launchpad (/dashboard)' },
  { value: 'studioApp', label: 'Studio app' },
  { value: 'dashboardConfig', label: 'Legacy App Builder runtime' },
  { value: 'crm', label: 'CRM dashboard' },
];

export default function TenantHomeDestinationPanel({
  tenantId,
  initialDestination,
  onSaved,
  dense = false,
}) {
  const [type, setType] = useState(initialDestination?.type || 'launchpad');
  const [id, setId] = useState(initialDestination?.id || '');
  const [apps, setApps] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const legacyOk = canAccessLegacyBuilders();

  useEffect(() => {
    setType(initialDestination?.type || 'launchpad');
    setId(initialDestination?.id || '');
  }, [initialDestination?.type, initialDestination?.id, tenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { apps: listed } = await listV2Apps();
        if (!cancelled) setApps(listed || []);
      } catch {
        if (!cancelled) setApps([]);
      }
      if (legacyOk) {
        try {
          const { data } = await listDashboardConfigs();
          if (!cancelled) setDashboards(Array.isArray(data) ? data : (data?.dashboards || []));
        } catch {
          if (!cancelled) setDashboards([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [legacyOk, tenantId]);

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await putTenantHomeDestination(tenantId, {
        type,
        id: type === 'studioApp' || type === 'dashboardConfig' ? id : null,
      });
      setSuccess('Home destination saved.');
      onSaved?.({ type, id: type === 'studioApp' || type === 'dashboardConfig' ? id : null });
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-testid="tenant-home-destination-panel" sx={{ mt: dense ? 0 : 1 }}>
      {!dense && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Post-login home
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Where users in this tenant land after sign-in. Unset or invalid targets use the solution launchpad.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}
      <Stack spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel id="home-type-label">Home type</InputLabel>
          <Select
            labelId="home-type-label"
            label="Home type"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setId('');
            }}
          >
            {HOME_TYPES.filter((t) => t.value !== 'dashboardConfig' || legacyOk).map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {type === 'studioApp' && (
          apps.length > 0 ? (
            <FormControl fullWidth size="small">
              <InputLabel id="home-app-label">Studio app</InputLabel>
              <Select
                labelId="home-app-label"
                label="Studio app"
                value={id}
                onChange={(e) => setId(e.target.value)}
              >
                {apps.map((a) => {
                  const aid = a._id || a.id;
                  return (
                    <MenuItem key={aid} value={aid}>{a.name || aid}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              fullWidth
              label="Studio app id"
              value={id}
              onChange={(e) => setId(e.target.value)}
              helperText="Paste a Studio V2 app ObjectId for this tenant"
            />
          )
        )}

        {type === 'dashboardConfig' && legacyOk && (
          dashboards.length > 0 ? (
            <FormControl fullWidth size="small">
              <InputLabel id="home-dash-label">App Builder dashboard</InputLabel>
              <Select
                labelId="home-dash-label"
                label="App Builder dashboard"
                value={id}
                onChange={(e) => setId(e.target.value)}
              >
                {dashboards.map((d) => {
                  const did = d._id || d.id;
                  return (
                    <MenuItem key={did} value={did}>{d.name || did}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              fullWidth
              label="Dashboard config id"
              value={id}
              onChange={(e) => setId(e.target.value)}
            />
          )
        )}

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || ((type === 'studioApp' || type === 'dashboardConfig') && !id)}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save home destination'}
        </Button>
      </Stack>
    </Box>
  );
}
